import { useForm } from "react-hook-form";
import { AppleButton } from "@/components/ui/apple-button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { FileUploadField } from "./asset-record/FileUploadField";
import { StatusSelect } from "./asset-record/StatusSelect";
import { PrioritySelect } from "./asset-record/PrioritySelect";
import { useFileUpload } from "./asset-record/useFileUpload";
import { 
  AssetRecordFormProps, 
  AssetRecordFormValues,
  RecordStatus,
  RecordPriority,
  RecordType,
  ApprovalStatus
} from "./asset-record/types";
import { cn, getStatusColor } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

export function AssetRecordForm({ 
  assetId, 
  initialData, 
  mode = "create", 
  onSuccess, 
  onCancel,
  isPublic = false
}: AssetRecordFormProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedImages, setSelectedImages] = useState<FileList | null>(null);
  const [selectedDocuments, setSelectedDocuments] = useState<FileList | null>(null);
  const { uploadFiles } = useFileUpload();
  const { t } = useTranslation();

  const form = useForm<AssetRecordFormValues>({
    defaultValues: {
      title: initialData?.title || "",
      details: initialData?.details || "",
      status: (initialData?.status as RecordStatus) || "Pending",
      priority: (initialData?.priority as RecordPriority) || "Medium",
      type: (initialData?.type as RecordType) || "Maintenance",
      location: initialData?.location || "",
      company_id: initialData?.company_id || null,
      place_id: initialData?.place_id || null,
      cost: initialData?.cost || null,
      progress: initialData?.progress || 0,
      due_date: initialData?.due_date || null,
      alert: initialData?.alert || false,
      approval_status: (initialData?.approval_status as ApprovalStatus) || "Pending",
      approval_by: initialData?.approval_by || null,
      approval_date: initialData?.approval_date || null,
      assigned_to: initialData?.assigned_to || null,
      category: initialData?.category || null,
      notes: initialData?.notes || null,
      phone: initialData?.phone || null,
      images: initialData?.images || [],
      documents: initialData?.documents || [],
    },
  });

  // Fetch companies for dropdown
  const { data: companies } = useQuery({
    queryKey: ["companies"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("companies")
        .select("*")
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  // Fetch places based on selected company
  const { data: places } = useQuery({
    queryKey: ["places", form.watch("company_id")],
    queryFn: async () => {
      const companyId = form.watch("company_id");
      if (!companyId) return [];
      const { data, error } = await supabase
        .from("places")
        .select("*")
        .eq("company_id", companyId)
        .order("name");
      if (error) throw error;
      return data;
    },
    enabled: !!form.watch("company_id"),
  });

  // Update form when initialData changes
  useEffect(() => {
    if (initialData) {
      Object.entries(initialData).forEach(([key, value]) => {
        form.setValue(key as keyof AssetRecordFormValues, value);
      });
    }
  }, [initialData, form]);

  const onSubmit = async (data: AssetRecordFormValues) => {
    if (isSubmitting) return;
    
    try {
      setIsSubmitting(true);
      // Handle file uploads
      let imageUrls: string[] = [];
      let documentUrls: string[] = [];

      if (selectedImages?.length) {
        imageUrls = await uploadFiles(selectedImages, 'asset-records');
      }

      if (selectedDocuments?.length) {
        documentUrls = await uploadFiles(selectedDocuments, 'asset-records');
      }

      // Transform and validate the data
      const submitData = {
        ...data,
        asset_id: assetId,
        user_id: isPublic ? null : (await supabase.auth.getSession()).data.session?.user?.id,
        images: imageUrls.length > 0 ? imageUrls : initialData?.images || [],
        documents: documentUrls.length > 0 ? documentUrls : initialData?.documents || [],
      };

      // Make sure we're not sending any extra properties that don't exist in the database
      const { assets, ...cleanedData } = { ...submitData, assets: undefined };

      if (mode === "create") {
        const { error } = await supabase
          .from("asset_records")
          .insert([cleanedData])
          .select()
          .single();
        
        if (error) throw error;
        toast({ title: "Record created successfully" });
      } else {
        const { error } = await supabase
          .from("asset_records")
          .update(cleanedData)
          .eq("id", initialData?.id)
          .select("*")
          .single();
        
        if (error) throw error;
        toast({ title: "Record updated successfully" });
      }

      onSuccess?.();
    } catch (error: any) {
      console.error("Error submitting asset record:", error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="overflow-y-auto max-h-[75vh]">
            <div className="px-4 py-6 space-y-8">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-apple-dark">{t('forms.record.basicInfo')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-apple-dark">{t('forms.record.title')}</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder={t('forms.record.placeholders.enterTitle')} 
                            {...field}
                            className={cn(
                              "rounded-apple",
                              "bg-apple-light-gray/20 border-apple-light-gray/30",
                              "placeholder:text-apple-gray",
                              "focus-visible:ring-apple-blue focus-visible:ring-offset-0"
                            )}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-apple-dark">{t('forms.record.type')}</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger className={cn(
                              "rounded-apple",
                              "bg-apple-light-gray/20 border-apple-light-gray/30",
                              "focus:ring-apple-blue focus:ring-offset-0"
                            )}>
                              <SelectValue placeholder={t('forms.record.placeholders.selectType')} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {/* Maintenance Types */}
                            <SelectItem value="Preventive Maintenance">Preventive Maintenance</SelectItem>
                            <SelectItem value="Corrective Maintenance">Corrective Maintenance</SelectItem>
                            <SelectItem value="Emergency Maintenance">Emergency Maintenance</SelectItem>
                            <SelectItem value="Scheduled Maintenance">Scheduled Maintenance</SelectItem>
                            <SelectItem value="Condition Maintenance">Condition Maintenance</SelectItem>

                            {/* Inspection Types */}
                            <SelectItem value="Safety Inspection">Safety Inspection</SelectItem>
                            <SelectItem value="Quality Inspection">Quality Inspection</SelectItem>
                            <SelectItem value="Compliance Inspection">Compliance Inspection</SelectItem>
                            <SelectItem value="Performance Inspection">Performance Inspection</SelectItem>

                            {/* Operation Types */}
                            <SelectItem value="Operation Check">Operation Check</SelectItem>
                            <SelectItem value="Calibration">Calibration</SelectItem>
                            <SelectItem value="Testing">Testing</SelectItem>
                            <SelectItem value="Training">Training</SelectItem>

                            {/* Incident Types */}
                            <SelectItem value="Breakdown">Breakdown</SelectItem>
                            <SelectItem value="Malfunction">Malfunction</SelectItem>
                            <SelectItem value="Damage Report">Damage Report</SelectItem>
                            <SelectItem value="Safety Incident">Safety Incident</SelectItem>

                            {/* Usage Types */}
                            <SelectItem value="Regular Usage">Regular Usage</SelectItem>
                            <SelectItem value="Special Usage">Special Usage</SelectItem>
                            <SelectItem value="Temporary Usage">Temporary Usage</SelectItem>
                            <SelectItem value="Rental Usage">Rental Usage</SelectItem>

                            {/* Service Types */}
                            <SelectItem value="Repair">Repair</SelectItem>
                            <SelectItem value="Upgrade">Upgrade</SelectItem>
                            <SelectItem value="Replacement">Replacement</SelectItem>
                            <SelectItem value="Installation">Installation</SelectItem>
                            <SelectItem value="Configuration">Configuration</SelectItem>

                            {/* Documentation Types */}
                            <SelectItem value="Certification">Certification</SelectItem>
                            <SelectItem value="Warranty Claim">Warranty Claim</SelectItem>
                            <SelectItem value="Insurance Claim">Insurance Claim</SelectItem>
                            <SelectItem value="Documentation Update">Documentation Update</SelectItem>

                            {/* Other */}
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="details"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-apple-dark">{t('forms.record.details')}</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder={t('forms.record.placeholders.enterDetails')}
                          {...field}
                          className={cn(
                            "rounded-apple min-h-[100px]",
                            "bg-apple-light-gray/20 border-apple-light-gray/30",
                            "placeholder:text-apple-gray",
                            "focus-visible:ring-apple-blue focus-visible:ring-offset-0"
                          )}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Status and Priority */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-apple-dark">{t('forms.record.statusPriority')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <StatusSelect form={form} />
                  <PrioritySelect form={form} />
                  <FormField
                    control={form.control}
                    name="progress"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-apple-dark">{t('forms.record.progress')}</FormLabel>
                        <FormControl>
                          <Input 
                            type="number"
                            min="0"
                            max="100"
                            placeholder={t('forms.record.placeholders.enterProgress')}
                            {...field}
                            onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : 0)}
                            className={cn(
                              "rounded-apple",
                              "bg-apple-light-gray/20 border-apple-light-gray/30",
                              "placeholder:text-apple-gray",
                              "focus-visible:ring-apple-blue focus-visible:ring-offset-0"
                            )}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Location Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-apple-dark">{t('forms.record.location')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="company_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-apple-dark">{t('forms.record.company')}</FormLabel>
                        <Select
                          onValueChange={(value) => {
                            field.onChange(Number(value));
                            form.setValue("place_id", null);
                          }}
                          value={field.value?.toString()}
                        >
                          <FormControl>
                            <SelectTrigger className={cn(
                              "rounded-apple",
                              "bg-apple-light-gray/20 border-apple-light-gray/30",
                              "focus:ring-apple-blue focus:ring-offset-0"
                            )}>
                              <SelectValue placeholder={t('forms.record.placeholders.selectCompany')} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {companies?.map((company) => (
                              <SelectItem key={company.id} value={company.id.toString()}>
                                {company.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="place_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-apple-dark">{t('forms.record.place')}</FormLabel>
                        <Select
                          onValueChange={(value) => field.onChange(Number(value))}
                          value={field.value?.toString()}
                          disabled={!form.watch("company_id")}
                        >
                          <FormControl>
                            <SelectTrigger className={cn(
                              "rounded-apple",
                              "bg-apple-light-gray/20 border-apple-light-gray/30",
                              "focus:ring-apple-blue focus:ring-offset-0"
                            )}>
                              <SelectValue placeholder={t('forms.record.placeholders.selectPlace')} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {places?.map((place) => (
                              <SelectItem key={place.id} value={place.id.toString()}>
                                {place.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="location"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-apple-dark">{t('forms.record.specificLocation')}</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder={t('forms.record.placeholders.enterLocation')}
                            {...field}
                            className={cn(
                              "rounded-apple",
                              "bg-apple-light-gray/20 border-apple-light-gray/30",
                              "placeholder:text-apple-gray",
                              "focus-visible:ring-apple-blue focus-visible:ring-offset-0"
                            )}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Assignment and Cost */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-apple-dark">{t('forms.record.assignmentCost')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="assigned_to"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-apple-dark">{t('forms.record.assignedTo')}</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder={t('forms.record.placeholders.enterAssignee')}
                            {...field}
                            className={cn(
                              "rounded-apple",
                              "bg-apple-light-gray/20 border-apple-light-gray/30",
                              "placeholder:text-apple-gray",
                              "focus-visible:ring-apple-blue focus-visible:ring-offset-0"
                            )}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="cost"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-apple-dark">{t('forms.record.cost')}</FormLabel>
                        <FormControl>
                          <Input 
                            type="number"
                            placeholder={t('forms.record.placeholders.enterCost')}
                            {...field}
                            onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                            className={cn(
                              "rounded-apple",
                              "bg-apple-light-gray/20 border-apple-light-gray/30",
                              "placeholder:text-apple-gray",
                              "focus-visible:ring-apple-blue focus-visible:ring-offset-0"
                            )}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-apple-dark">{t('forms.record.phone')}</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder={t('forms.record.placeholders.enterPhone')}
                            {...field}
                            className={cn(
                              "rounded-apple",
                              "bg-apple-light-gray/20 border-apple-light-gray/30",
                              "placeholder:text-apple-gray",
                              "focus-visible:ring-apple-blue focus-visible:ring-offset-0"
                            )}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="due_date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-apple-dark">{t('forms.record.dueDate')}</FormLabel>
                        <FormControl>
                          <Input 
                            type="date"
                            {...field}
                            className={cn(
                              "rounded-apple",
                              "bg-apple-light-gray/20 border-apple-light-gray/30",
                              "placeholder:text-apple-gray",
                              "focus-visible:ring-apple-blue focus-visible:ring-offset-0"
                            )}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Approval Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-apple-dark">{t('forms.record.approvalInfo')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="approval_status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-apple-dark">{t('forms.record.approvalStatus')}</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger className={cn(
                              "rounded-apple",
                              "bg-apple-light-gray/20 border-apple-light-gray/30",
                              "focus:ring-apple-blue focus:ring-offset-0",
                              field.value && getStatusColor(field.value)
                            )}>
                              <SelectValue placeholder={t('forms.record.placeholders.selectStatus')} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Pending" className={getStatusColor('Pending')}>Pending</SelectItem>
                            <SelectItem value="Approved" className={getStatusColor('Approved')}>Approved</SelectItem>
                            <SelectItem value="Rejected" className={getStatusColor('Rejected')}>Rejected</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="approval_by"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-apple-dark">{t('forms.record.approvedBy')}</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder={t('forms.record.placeholders.enterApprover')}
                            {...field}
                            className={cn(
                              "rounded-apple",
                              "bg-apple-light-gray/20 border-apple-light-gray/30",
                              "placeholder:text-apple-gray",
                              "focus-visible:ring-apple-blue focus-visible:ring-offset-0"
                            )}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="approval_date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-apple-dark">{t('forms.record.approvalDate')}</FormLabel>
                        <FormControl>
                          <Input 
                            type="date"
                            {...field}
                            className={cn(
                              "rounded-apple",
                              "bg-apple-light-gray/20 border-apple-light-gray/30",
                              "placeholder:text-apple-gray",
                              "focus-visible:ring-apple-blue focus-visible:ring-offset-0"
                            )}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="alert"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>{t('forms.record.setAlert')}</FormLabel>
                          <p className="text-sm text-muted-foreground">
                            {t('forms.record.alertDesc')}
                          </p>
                        </div>
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Additional Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-apple-dark">{t('forms.record.additionalInfo')}</h3>
                <div className="grid grid-cols-1 gap-4">
                  <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-apple-dark">{t('forms.record.category')}</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder={t('forms.record.placeholders.enterCategory')}
                            {...field}
                            className={cn(
                              "rounded-apple",
                              "bg-apple-light-gray/20 border-apple-light-gray/30",
                              "placeholder:text-apple-gray",
                              "focus-visible:ring-apple-blue focus-visible:ring-offset-0"
                            )}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="notes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-apple-dark">{t('forms.record.notes')}</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder={t('forms.record.placeholders.enterNotes')}
                            {...field}
                            className={cn(
                              "rounded-apple min-h-[100px]",
                              "bg-apple-light-gray/20 border-apple-light-gray/30",
                              "placeholder:text-apple-gray",
                              "focus-visible:ring-apple-blue focus-visible:ring-offset-0"
                            )}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Attachments */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-apple-dark">{t('forms.record.attachments')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FileUploadField
                    label={t('forms.record.images')}
                    accept="image/*"
                    onChange={setSelectedImages}
                    existingFiles={initialData?.images}
                  />

                  <FileUploadField
                    label={t('forms.record.documents')}
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={setSelectedDocuments}
                    existingFiles={initialData?.documents}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="sticky bottom-0 bg-white border-t p-4">
            <div className="flex justify-end gap-4">
              <AppleButton
                type="button"
                variant="secondary"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                {t('forms.record.buttons.cancel')}
              </AppleButton>
              <AppleButton 
                type="submit"
                disabled={isSubmitting}
                className="bg-blue-600 hover:bg-blue-700 text-white min-w-[100px]"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {mode === "create" ? t('forms.record.buttons.creating') : t('forms.record.buttons.updating')}
                  </>
                ) : (
                  mode === "create" ? t('forms.record.buttons.create') : t('forms.record.buttons.update')
                )}
              </AppleButton>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}